import React from 'react';
import { Input } from '../../ui/input';
import CommonFormFields from './CommonFormFields';

/**
 * Home Support Form - Non-medical home assistance (cleaning, organizing, etc.)
 */
const HomeSupportForm = ({ formData, setFormData }) => {
  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="space-y-4">
      <CommonFormFields formData={formData} setFormData={setFormData} />
      
      <hr className="my-4" />
      <h4 className="font-bold text-lg">Service Details</h4>
      
      <div>
        <label className="block text-sm font-medium mb-2">Service Category *</label>
        <select
          value={formData.service_category || ''}
          onChange={(e) => updateField('service_category', e.target.value)}
          className="w-full p-3 border rounded-xl"
          required
        >
          <option value="">Select category</option>
          <option value="cleaning">Cleaning</option>
          <option value="laundry">Laundry</option>
          <option value="organizing">Organizing</option>
          <option value="general_help">General Help</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Tasks Requested *</label>
        <textarea
          value={formData.tasks_text || ''}
          onChange={(e) => {
            setFormData({
              ...formData,
              tasks_text: e.target.value,
              tasks_requested: e.target.value.split('\n').filter(t => t.trim())
            });
          }}
          className="w-full p-3 border rounded-xl min-h-[100px]"
          placeholder="List specific tasks (one per line):&#10;Vacuum living room&#10;Clean kitchen counters&#10;Organize closet"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Task Details</label>
        <textarea
          value={formData.task_details || ''}
          onChange={(e) => updateField('task_details', e.target.value)}
          className="w-full p-3 border rounded-xl min-h-[80px]"
          placeholder="Any additional details about the tasks"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Estimated Duration (hours)</label>
        <Input
          type="number"
          step="0.5"
          min="1"
          value={formData.estimated_duration || 2}
          onChange={(e) => updateField('estimated_duration', parseFloat(e.target.value))}
        />
      </div>
      
      <hr className="my-4" />
      <h4 className="font-bold text-lg">Location</h4>
      
      <div>
        <label className="block text-sm font-medium mb-2">Service Address *</label>
        <Input
          value={formData.service_address || ''}
          onChange={(e) => updateField('service_address', e.target.value)}
          placeholder="Full address"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Access Instructions</label>
        <Input
          value={formData.access_instructions || ''}
          onChange={(e) => updateField('access_instructions', e.target.value)}
          placeholder="Gate code, key location, etc."
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Home Type</label>
          <select
            value={formData.home_type || 'house'}
            onChange={(e) => updateField('home_type', e.target.value)}
            className="w-full p-3 border rounded-xl"
          >
            <option value="house">House</option>
            <option value="apartment">Apartment</option>
            <option value="condo">Condo</option>
            <option value="townhouse">Townhouse</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Square Footage</label>
          <Input
            value={formData.square_footage || ''}
            onChange={(e) => updateField('square_footage', e.target.value)}
            placeholder="Approximate"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2"># of Rooms</label>
          <Input
            type="number"
            value={formData.number_of_rooms || ''}
            onChange={(e) => updateField('number_of_rooms', parseInt(e.target.value))}
          />
        </div>
      </div>
      
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.pets_present || false}
            onChange={(e) => updateField('pets_present', e.target.checked)}
            className="w-4 h-4"
          />
          Pets Present
        </label>
        {formData.pets_present && (
          <Input
            value={formData.pet_details || ''}
            onChange={(e) => updateField('pet_details', e.target.value)}
            placeholder="Pet type, name, any concerns"
            className="mt-2"
          />
        )}
      </div>
      
      <hr className="my-4" />
      <h4 className="font-bold text-lg">Supplies</h4>
      
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.customer_provides_supplies || false}
          onChange={(e) => updateField('customer_provides_supplies', e.target.checked)}
          className="w-4 h-4"
        />
        I will provide cleaning supplies
      </label>
      
      <hr className="my-4" />
      <h4 className="font-bold text-lg">Recurring?</h4>
      
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.is_recurring || false}
          onChange={(e) => updateField('is_recurring', e.target.checked)}
          className="w-4 h-4"
        />
        Make this a recurring service
      </label>
      
      {formData.is_recurring && (
        <select
          value={formData.recurring_frequency || 'weekly'}
          onChange={(e) => updateField('recurring_frequency', e.target.value)}
          className="w-full p-3 border rounded-xl mt-2"
        >
          <option value="weekly">Weekly</option>
          <option value="biweekly">Bi-Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      )}
    </div>
  );
};

export default HomeSupportForm;
